'use client'
import { createContext, useContext, useEffect, useState } from 'react'
import type { Lang } from '@/lib/i18n'

/**
 * Per-page language-counterpart URLs, registered by blog pages so LangToggle
 * can navigate to the right translation (or a sensible fallback) instead of
 * doing the homepage's shallow / ↔ /fa URL swap. `null` = not on a page with
 * alternates (homepage behavior applies).
 */
export type AlternateLinks = Partial<Record<Lang, string>>

type AlternateLinksCtx = {
  alternates: AlternateLinks | null
  setAlternates: (links: AlternateLinks | null) => void
}

const Ctx = createContext<AlternateLinksCtx>({
  alternates: null,
  setAlternates: () => {},
})

export function AlternateLinksProvider({ children }: { children: React.ReactNode }) {
  const [alternates, setAlternates] = useState<AlternateLinks | null>(null)
  return <Ctx.Provider value={{ alternates, setAlternates }}>{children}</Ctx.Provider>
}

export function useAlternateLinks() {
  return useContext(Ctx)
}

/**
 * Registrar — rendered by server pages with computed hrefs:
 *   <RegisterAlternateLinks en="/blog/foo" fa="/fa/blog/بار" />
 * Registers on mount, clears on unmount (leaving the blog restores the
 * homepage toggle behavior).
 */
export function RegisterAlternateLinks({ en, fa }: AlternateLinks) {
  const { setAlternates } = useAlternateLinks()
  useEffect(() => {
    setAlternates({ en, fa })
    return () => setAlternates(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [en, fa])
  return null
}
